// Reusable building blocks for the Order/Job detail pages: back link, labeled
// field rows, a status timeline, an external map link, and a read-only chat
// thread that mirrors the in-app customer/driver conversation.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { db } from '../lib/firebase';
import { formatDateTime } from '../lib/format';
import StatusBadge from './StatusBadge';

export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 transition hover:text-slate-800"
    >
      <ArrowLeft size={15} /> {label}
    </Link>
  );
}

export interface Person {
  name: string;
  email?: string;
  phone?: string;
}

/** Resolves a user's display name/email/phone from `users/{uid}` (one-shot). */
export function usePerson(uid?: string): Person | null {
  const [person, setPerson] = useState<Person | null>(null);
  useEffect(() => {
    if (!uid) {
      setPerson(null);
      return;
    }
    let active = true;
    getDoc(doc(db, 'users', uid))
      .then((snap) => {
        if (!active) return;
        const d = snap.data() as Record<string, unknown> | undefined;
        if (!d) {
          setPerson({ name: uid });
          return;
        }
        const name =
          [d.firstName, d.lastName].filter(Boolean).join(' ') ||
          (typeof d.email === 'string' ? d.email : '') ||
          uid;
        setPerson({
          name,
          email: typeof d.email === 'string' ? d.email : undefined,
          phone: typeof d.phone === 'string' ? d.phone : undefined,
        });
      })
      .catch(() => {
        if (active) setPerson({ name: uid });
      });
    return () => {
      active = false;
    };
  }, [uid]);
  return person;
}

export function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value?: React.ReactNode;
  mono?: boolean;
}) {
  const empty =
    value === undefined ||
    value === null ||
    value === '' ||
    value === '—';
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <div className="shrink-0 text-xs font-medium uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div
        className={`min-w-0 break-words text-right text-sm text-slate-800 ${
          mono ? 'font-mono text-xs' : ''
        }`}
      >
        {empty ? <span className="text-slate-400">—</span> : value}
      </div>
    </div>
  );
}

export interface TimelineEntry {
  status?: string;
  timestamp?: unknown;
  notes?: string | null;
  updatedBy?: string | null;
}

export function Timeline({ entries }: { entries: TimelineEntry[] }) {
  if (!entries.length) {
    return <p className="text-sm text-slate-400">No history recorded yet.</p>;
  }
  return (
    <ol className="relative space-y-4 border-l border-slate-200 pl-4">
      {entries.map((e, i) => (
        <li key={i} className="relative">
          <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-brand-400 ring-1 ring-brand-200" />
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={e.status} />
            <span className="text-xs text-slate-400">
              {formatDateTime(e.timestamp)}
            </span>
          </div>
          {e.notes && (
            <p className="mt-0.5 text-sm text-slate-600">{e.notes}</p>
          )}
          {e.updatedBy && (
            <p className="mt-0.5 text-xs text-slate-400">by {e.updatedBy}</p>
          )}
        </li>
      ))}
    </ol>
  );
}

export function MapLink({
  lat,
  lng,
  children,
}: {
  lat?: number;
  lng?: number;
  children: React.ReactNode;
}) {
  if (
    typeof lat !== 'number' ||
    typeof lng !== 'number' ||
    (lat === 0 && lng === 0)
  ) {
    return <>{children}</>;
  }
  return (
    <a
      href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-brand-600 hover:underline"
    >
      {children}
      <ExternalLink size={12} className="shrink-0" />
    </a>
  );
}

interface ChatMsg {
  id: string;
  senderName?: string;
  senderRole?: string;
  message?: string;
  timestamp?: unknown;
}

/** Read-only view of the customer/driver conversation for an order. */
export function ChatThread({ orderId }: { orderId: string }) {
  const [msgs, setMsgs] = useState<ChatMsg[] | null>(null);

  useEffect(() => {
    if (!orderId) {
      setMsgs([]);
      return;
    }
    const q = query(
      collection(db, 'chats', orderId, 'messages'),
      orderBy('timestamp', 'asc'),
      limit(200)
    );
    const unsub = onSnapshot(
      q,
      (snap) =>
        setMsgs(snap.docs.map((d) => ({ ...(d.data() as ChatMsg), id: d.id }))),
      () => setMsgs([])
    );
    return unsub;
  }, [orderId]);

  if (msgs === null) {
    return <p className="text-sm text-slate-400">Loading messages…</p>;
  }
  if (msgs.length === 0) {
    return <p className="text-sm text-slate-400">No messages exchanged.</p>;
  }

  return (
    <div className="space-y-2">
      {msgs.map((m) => {
        const isDriver = m.senderRole === 'driver';
        return (
          <div
            key={m.id}
            className={`flex ${isDriver ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                isDriver ? 'bg-brand-50 text-slate-800' : 'bg-slate-100 text-slate-800'
              }`}
            >
              <div className="mb-0.5 flex items-center gap-2 text-[11px] text-slate-400">
                <span className="font-medium text-slate-500">
                  {m.senderName || (isDriver ? 'Driver' : 'Customer')}
                </span>
                <span>{formatDateTime(m.timestamp)}</span>
              </div>
              <div className="whitespace-pre-wrap break-words">{m.message}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
